import { useEffect } from "react";
import { getConsent, onConsentChange } from "../lib/consent";

/**
 * Conditional loader for third-party tracking scripts.
 * - Scripts are NEVER loaded before the user has granted consent.
 * - Toggled live when the user changes preferences.
 *
 * To activate: replace the placeholder IDs in /app/frontend/.env:
 *   REACT_APP_GA4_ID=G-XXXXXXX
 *   REACT_APP_META_PIXEL_ID=1234567890
 *   REACT_APP_GTM_ID=GTM-XXXXXX
 * Leaving them empty keeps the category ready but loads nothing.
 */
const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const GTM_ID = process.env.REACT_APP_GTM_ID || "";

const loadScript = (id, src, inline) => {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  if (src) s.src = src;
  // Use textContent (not innerHTML) to avoid HTML parsing / XSS surface.
  // Inline content set this way is still executed by the browser when the
  // <script> element is appended to the DOM.
  if (inline) s.textContent = inline;
  document.head.appendChild(s);
};

const removeScript = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

const enableAnalytics = () => {
  if (GA4_ID) {
    loadScript("ga4-loader", `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
    loadScript(
      "ga4-init",
      null,
      `window.dataLayer = window.dataLayer || [];
       function gtag(){dataLayer.push(arguments);}
       gtag('js', new Date());
       gtag('config', '${GA4_ID}', { anonymize_ip: true });`
    );
  }
  if (GTM_ID) {
    loadScript(
      "gtm-init",
      null,
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
       new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
       j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
       'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
       })(window,document,'script','dataLayer','${GTM_ID}');`
    );
  }
};

const disableAnalytics = () => {
  ["ga4-loader", "ga4-init", "gtm-init"].forEach(removeScript);
  // Also tell GA to stop tracking in case it already loaded
  if (GA4_ID && typeof window !== "undefined") {
    window[`ga-disable-${GA4_ID}`] = true;
  }
};

const enableMarketing = () => {
  if (META_PIXEL_ID) {
    loadScript(
      "meta-pixel-init",
      null,
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
       n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
       n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
       t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
       document,'script','https://connect.facebook.net/en_US/fbevents.js');
       fbq('init', '${META_PIXEL_ID}');
       fbq('track', 'PageView');`
    );
  }
};

const disableMarketing = () => {
  removeScript("meta-pixel-init");
};

const AnalyticsScripts = () => {
  useEffect(() => {
    const apply = (consent) => {
      if (!consent) {
        disableAnalytics();
        disableMarketing();
        return;
      }
      if (consent.analytics) enableAnalytics();
      else disableAnalytics();
      if (consent.marketing) enableMarketing();
      else disableMarketing();
    };

    apply(getConsent());
    return onConsentChange(apply);
  }, []);

  return null;
};

export default AnalyticsScripts;
